import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  fetchUsers,
  createUser,
  updateUser as apiUpdateUser,
  deleteUser as apiDeleteUser,
  fetchDomains,
  createDomain,
  updateDomain as apiUpdateDomain,
  deleteDomain as apiDeleteDomain,
  fetchProjects,
  createProject,
  updateProject as apiUpdateProject,
  deleteProject as apiDeleteProject,
} from '../../api/admin';

/** Display order in admin (matches public portfolio: MoreMedDiet, MedAgriCycle, Techoney). */
export const SHOWCASE_PROJECT_DISPLAY_ORDER = ['p_seed_1', 'p_seed_3', 'p_seed_2'];

/** Default portfolio showcase rows — same ids as legacy defaults; editable/deletable in admin. */
export const SHOWCASE_PROJECT_IDS = new Set(['p_seed_1', 'p_seed_2', 'p_seed_3']);

const AdminDataContext = createContext(null);

export function AdminDataProvider({ children }) {
  const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, dRes, pRes] = await Promise.allSettled([fetchUsers(), fetchDomains(), fetchProjects()]);
      if (uRes.status === 'fulfilled') setUsers(uRes.value.users || []);
      if (dRes.status === 'fulfilled') setDomains(dRes.value.domains || []);
      if (pRes.status === 'fulfilled') setProjects(pRes.value.projects || []);
    } catch (err) {
      console.error('[AdminDataProvider]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const addUser = useCallback(async (row) => {
    const res = await createUser(row);
    setUsers((prev) => [...prev, res.user]);
    return res.user.id;
  }, []);

  const updateUser = useCallback(async (id, row) => {
    const res = await apiUpdateUser(id, row);
    setUsers((prev) => prev.map((u) => (u.id === id ? res.user : u)));
  }, []);

  const deleteUser = useCallback(async (id) => {
    await apiDeleteUser(id);
    setUsers((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const addDomain = useCallback(async (row) => {
    const res = await createDomain(row);
    setDomains((prev) => [...prev, res.domain]);
  }, []);

  const updateDomain = useCallback(async (id, row) => {
    const res = await apiUpdateDomain(id, row);
    setDomains((prev) => prev.map((d) => (d.id === id ? res.domain : d)));
  }, []);

  const deleteDomain = useCallback(async (id) => {
    await apiDeleteDomain(id);
    setDomains((prev) => prev.filter((d) => d.id !== id));
    setProjects((prev) => prev.filter((p) => p.domainId !== id));
  }, []);

  const domainInUse = useCallback(
    (domainId) => projects.some((p) => p.domainId === domainId),
    [projects],
  );

  const addProject = useCallback(async (row) => {
    const res = await createProject(row);
    setProjects((prev) => [...prev, res.project]);
  }, []);

  const updateProject = useCallback(async (id, row) => {
    const res = await apiUpdateProject(id, row);
    setProjects((prev) => prev.map((p) => (p.id === id ? res.project : p)));
  }, []);

  const deleteProject = useCallback(async (id) => {
    await apiDeleteProject(id);
    setProjects((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const value = useMemo(
    () => ({
      users,
      domains,
      projects,
      loading,
      refreshAll,
      addUser,
      updateUser,
      deleteUser,
      addDomain,
      updateDomain,
      deleteDomain,
      domainInUse,
      addProject,
      updateProject,
      deleteProject,
    }),
    [
      users,
      domains,
      projects,
      loading,
      refreshAll,
      addUser,
      updateUser,
      deleteUser,
      addDomain,
      updateDomain,
      deleteDomain,
      domainInUse,
      addProject,
      updateProject,
      deleteProject,
    ],
  );

  return <AdminDataContext.Provider value={value}>{children}</AdminDataContext.Provider>;
}

export function useAdminData() {
  const ctx = useContext(AdminDataContext);
  if (!ctx) {
    throw new Error('useAdminData must be used within AdminDataProvider');
  }
  return ctx;
}
